<script setup lang="ts">
import type { LatLngExpression, Map as LeafletMap, Marker as LeafletMarker } from 'leaflet'

import 'leaflet/dist/leaflet.css'

const { t } = useI18n()

const props = defineProps<{
  center: [number, number]
  zoom?: number
  markers?: {
    id?: number
    position: [number, number]
    text?: string
    type?: 'atm' | 'terminal' | string
  }[]
}>()

const emit = defineEmits<{
  'marker-click': [markerId: number]
}>()

const TILE_URL = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png'
const TILE_ATTRIBUTION = '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a> contributors'

const MARKER_SVG = `
  <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
    <ellipse cx="18" cy="34" rx="10" ry="2" fill="rgba(0,0,0,0.15)"/>
    <path d="M18 3C11.925 3 7 7.925 7 14c0 5 9 19 11 19s11-14 11-19c0-6.075-4.925-11-11-11z" fill="url(#markerGrad)"/>
    <circle cx="18" cy="14" r="4" fill="white" stroke="rgba(59,130,246,0.3)" stroke-width="1"/>
    <circle cx="18" cy="14" r="2" fill="#2563eb"/>
    <defs>
      <linearGradient id="markerGrad" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" style="stop-color:#3b82f6;stop-opacity:1" />
        <stop offset="100%" style="stop-color:#1e40af;stop-opacity:1" />
      </linearGradient>
    </defs>
  </svg>
`

const mapContainer = ref<HTMLElement | null>(null)
let L: typeof import('leaflet') | null = null
let map: LeafletMap | null = null
let markersById: Record<number, LeafletMarker> = {}
let resizeObserver: ResizeObserver | null = null

// Leaflet expects [lat, lng], props use [lng, lat]
const toLatLng = ([lng, lat]: [number, number]): LatLngExpression => [lat, lng]

const setMarkersOpacity = (activeId: number | null) => {
  Object.entries(markersById).forEach(([id, marker]) => {
    marker.setOpacity(activeId === null || Number(id) === activeId ? 1.0 : 0.5)
  })
}

function renderMarkers() {
  if (!L || !map) return

  Object.values(markersById).forEach((marker) => marker.remove())
  markersById = {}

  const icon = L.divIcon({
    html: MARKER_SVG,
    className: 'map-marker',
    iconSize: [36, 36],
    iconAnchor: [18, 36],
    popupAnchor: [0, -34]
  })

  props.markers?.forEach((marker, index) => {
    if (!L || !map) return

    const markerId = marker.id || index
    const leafletMarker = L.marker(toLatLng(marker.position), {
      icon,
      title: marker.text?.replace(/<[^>]*>/g, '').trim() || t('widget.map.default_marker_title')
    }).addTo(map)

    markersById[markerId] = leafletMarker

    if (marker.text) {
      leafletMarker.bindPopup(`<div class="p-2 max-w-xs">${marker.text}</div>`, {
        className: 'map-popup',
        maxWidth: 320
      })
      leafletMarker.on('popupopen', () => setMarkersOpacity(markerId))
      leafletMarker.on('click', () => emit('marker-click', markerId))
    }
  })
}

// Show popup for a specific marker (triggered from the branch list)
const showMarkerPopup = (markerId: number) => {
  markersById[markerId]?.openPopup()
}

const onShowMarkerPopup = (event: Event) => {
  const { markerId } = (event as CustomEvent<{ markerId: number }>).detail
  showMarkerPopup(markerId)
}

onMounted(async () => {
  try {
    L = await import('leaflet')
    if (!mapContainer.value) return

    map = L.map(mapContainer.value, {
      center: toLatLng(props.center),
      zoom: props.zoom || 13,
      // Do not hijack page scrolling until the user interacts with the map
      scrollWheelZoom: false
    })

    L.tileLayer(TILE_URL, { maxZoom: 19, attribution: TILE_ATTRIBUTION }).addTo(map)

    map.once('click focus', () => map?.scrollWheelZoom.enable())
    map.on('popupclose', () => setMarkersOpacity(null))

    renderMarkers()

    // Leaflet does not track container size; layout shifts after init would move the center
    resizeObserver = new ResizeObserver(() => map?.invalidateSize())
    resizeObserver.observe(mapContainer.value)

    mapContainer.value.addEventListener('show-marker-popup', onShowMarkerPopup)
  } catch (e) {
    console.error('Failed to load map', e)
  }
})

onBeforeUnmount(() => {
  resizeObserver?.disconnect()
  resizeObserver = null
  mapContainer.value?.removeEventListener('show-marker-popup', onShowMarkerPopup)
  map?.remove()
  map = null
  markersById = {}
})

// Watch for center changes
watch(
  () => props.center,
  (newCenter) => {
    map?.panTo(toLatLng(newCenter))
  }
)

// Watch for markers changes
watch(
  () => props.markers,
  () => {
    if (map) {
      renderMarkers()
      map.panTo(toLatLng(props.center))
    }
  },
  { deep: true }
)
</script>

<template>
  <div ref="mapContainer" class="map-widget isolate h-[800px] w-full overflow-hidden rounded-lg" />
</template>

<style>
.map-marker {
  @apply border-0 bg-transparent;
}
</style>
